import { Divider } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../../../components/custom_components/Modal'
import { ConclusionTargetModal } from '../../../components/custom_components/ObjectiveModal/ConclusionTargetModal'
import PerSpacer from '../../../components/custom_components/Spacer'
import PerText from '../../../components/custom_components/Text'
import { useAuth } from '../../../context/auth-context'
import useObjectiveWithModalInMeeting from '../../../graphql/objectives/useObjectiveWithModalInMeeting'
import { EmployeeDisplayFragment, ObjectiveStatus, ObjectiveDto } from '../../../types'
import { OkrModalTitle } from './components/OkrModalTitle'
import { ObjectiveList, ObjectiveListType } from './lists/ObjectivesList'
import { filterObjectivesByTypeStatusAndRefYear } from './util'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { IconIndicator } from '../../../components/custom_components/IconIndicator/IconIndicator'

interface Props {
  employee: EmployeeDisplayFragment
  isEmployeePrepared: boolean
  meetingId: string
  objectives: ObjectiveDto[]
  allowedNewTargets: boolean
}

const ConclusionNewObjectives: FC<Props> = ({ employee, meetingId, objectives, isEmployeePrepared, allowedNewTargets }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { open, close, objective, handleObjectiveChange } = useObjectiveWithModalInMeeting({ meetingId })

  // we filter proposal targets if employee didn't confirm preparation (reviewer can't see these targets so they aren't valid)
  const includedDevelopmentObjectiveStatuses = isEmployeePrepared
    ? [ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager]
    : [ObjectiveStatus.ProposalFromManager]

  const monetaryObjectives = filterObjectivesByTypeStatusAndRefYear(objectives, true, [
    ObjectiveStatus.Proposal,
    ObjectiveStatus.ProposalFromManager,
  ])

  const devObjectives = filterObjectivesByTypeStatusAndRefYear(objectives, false, includedDevelopmentObjectiveStatuses)
  return (
    <>
      {(!!monetaryObjectives.length || !!devObjectives.length) && (
        <div data-e2e-new-targets-conclusion>
          {objective && (
            <PerModal
              visible={open}
              onCancel={close}
              size="large"
              title={{ custom: <OkrModalTitle title={objective && objective.title} objective={objective} /> }}
            >
              <ConclusionTargetModal objective={objective} employeeId={employee.id} user={user} />
            </PerModal>
          )}

          <PerFlexRow align="middle" mb="s">
            <PerText appearance="heading" color="primary">
              {t('appraisalMeeting.stepConclusion.newTargets')}
            </PerText>
            {!allowedNewTargets && !!monetaryObjectives.length && (
              <>
                <PerSpacer ml="xs" />
                <IconIndicator
                  icon="warning_fill"
                  background="error"
                  variant="clickable"
                  size="l"
                  tooltip={t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingWrongNewTargets')}
                />
              </>
            )}
          </PerFlexRow>

          {!!monetaryObjectives.length && (
            <PerSpacer mb="m">
              <ObjectiveList
                listType={ObjectiveListType.Monetary}
                isManagerPreparation={false}
                objectives={monetaryObjectives}
                disabled={true}
                handleObjectiveClicked={handleObjectiveChange}
                showTasks
                hideAction
                showStartDate
              />
            </PerSpacer>
          )}
          {!!devObjectives.length && (
            <ObjectiveList
              listType={ObjectiveListType.Development}
              objectives={devObjectives}
              isManagerPreparation={false}
              disabled={true}
              handleObjectiveClicked={handleObjectiveChange}
              showTasks
              hideAction
              showStartDate
            />
          )}

          <Divider />
        </div>
      )}
    </>
  )
}
export { ConclusionNewObjectives }
